import moment from 'moment-timezone'
import shiftsHelper from '@/helpers/shifts'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: shiftActions } = createNamespacedHelpers('shifts')

export default {
  data() {
    return {
      estimated_lunch_end: null,
      estimated_lunch_start: null,
      estimated_second_lunch_end: null,
      estimated_second_lunch_start: null
    }
  },
  mixins: [shiftsHelper],
  watch: {
    'shiftForm.work_start': {
      handler(newVal, oldVal) {
        if (!oldVal || !oldVal.isSame(newVal)) this.fillHours()

        if (this.shiftForm.set_scheduled_as_signed) {
          this.shiftForm.scheduled_start = newVal
        }
        this.refreshComputedDuration(this.signedDuration)
      }
    },

    'shiftForm.work_end': {
      handler(newVal, oldVal) {
        if (!oldVal || !oldVal.isSame(newVal)) this.fillHours()

        if (this.shiftForm.set_scheduled_as_signed) {
          this.shiftForm.scheduled_end = newVal
        }
        this.refreshComputedDuration(this.signedDuration)
      }
    },

    'shiftForm.lunch_start': {
      handler(newVal, oldVal) {
        if (!oldVal || !oldVal.isSame(newVal)) this.fillHours()

        if (this.shiftForm.set_scheduled_as_signed) {
          this.shiftForm.scheduled_lunch_start = newVal
        }
        this.refreshComputedDuration(this.lunchDuration)
      }
    },

    'shiftForm.lunch_end': {
      handler(newVal, oldVal) {
        if (!oldVal || !oldVal.isSame(newVal)) this.fillHours()

        if (this.shiftForm.set_scheduled_as_signed) {
          this.shiftForm.scheduled_lunch_end = newVal
        }
        this.refreshComputedDuration(this.lunchDuration)
      }
    },

    'shiftForm.second_lunch_start': {
      handler(newVal, oldVal) {
        if (!oldVal || !oldVal.isSame(newVal)) this.fillHours()

        if (this.shiftForm.set_scheduled_as_signed) {
          this.shiftForm.scheduled_second_lunch_start = newVal
        }
        this.refreshComputedDuration(this.secondLunchDuration)
      }
    },

    'shiftForm.second_lunch_end': {
      async handler(newVal, oldVal) {
        if (!oldVal || !oldVal.isSame(newVal)) await this.fillHours()

        if (this.shiftForm.set_scheduled_as_signed) {
          this.shiftForm.scheduled_second_lunch_end = newVal
        }
        this.refreshComputedDuration(this.secondLunchDuration)
      }
    }
  },
  computed: {
    // formatters

    dateTimeFormat() {
      return this.$store.state.applicationState.dateTimeFormat
    },
    dateFormat() {
      return this.$store.state.applicationState.dateFormat
    },
    timeFormat() {
      return this.$store.state.applicationState.timeFormat
    },
    timeZone() {
      return this.$store.state.applicationState.timeZone
    },
    minLunchDuration() {
      return this.$store.state.applicationState.timeConstants.lunch_duration
    },
    minSecondLunchDuration() {
      return this.$store.state.applicationState.timeConstants.second_lunch_duration
    },
    minWorkDuration() {
      return this.$store.state.applicationState.timeConstants.min_work_duration
    },
    minWorkWithWaiverDuration() {
      return this.$store.state.applicationState.timeConstants.min_work_with_waiver_duration
    },
    maxWorkDuration() {
      return this.$store.state.applicationState.timeConstants.max_work_duration
    },
    longWorkDuration() {
      return this.$store.state.applicationState.timeConstants.long_work_duration
    },
    longWorkWithWaiverDuration() {
      return this.$store.state.applicationState.timeConstants.long_work_with_waiver_duration
    },
    afterShiftEndGracePeriod() {
      return this.$store.state.applicationState.timeConstants.after_shift_end_grace_period
    },
    afterShiftStartGracePeriod() {
      return this.$store.state.applicationState.timeConstants.after_shift_start_grace_period
    },
    beforeShiftEndGracePeriod() {
      return this.$store.state.applicationState.timeConstants.before_shift_end_grace_period
    },
    beforeShiftStartGracePeriod() {
      return this.$store.state.applicationState.timeConstants.before_shift_start_grace_period
    },
    // available fields

    isWorkEmpty() {
      return !this.shiftForm.work_start || !this.shiftForm.work_end
    },

    isLunchEmpty() {
      return !this.shiftForm.lunch_start || !this.shiftForm.lunch_end
    },

    isSecondLunchEmpty() {
      return !this.shiftForm.second_lunch_start || !this.shiftForm.second_lunch_end
    },

    isLunchDisabled() {
      return this.isWorkEmpty || this.isLunchWaived
    },

    isLunchEndDisabled() {
      return this.isLunchDisabled || !this.shiftForm.lunch_start
    },

    isSecondLunchDisabled() {
      return this.isWorkEmpty || (this.isLunchEmpty && !this.isLunchWaived) || this.isSecondLunchWaived
    },

    isSecondLunchEndDisabled() {
      return this.isSecondLunchDisabled || !this.shiftForm.second_lunch_start
    },

    isLunchWaiveAvailable() {
      if (this.isWorkEmpty) {
        this.isLunchWaived = false

        return false
      }

      const calculation =
        this.signedDuration >= this.minWorkDuration && this.signedDuration < this.minWorkWithWaiverDuration

      if (!calculation) this.isLunchWaived = false

      return calculation
    },

    isSecondLunchWaiveAvailable() {
      if (this.isWorkEmpty || this.isLunchEmpty) {
        this.isSecondLunchWaived = false

        return false
      }

      const calculation =
        this.signedDuration - this.lunchDuration >= this.longWorkDuration &&
        this.signedDuration - this.lunchDuration < this.longWorkWithWaiverDuration

      if (!calculation) this.isSecondLunchWaived = false

      return calculation
    },

    // alerts
    lunchWaiveInfo() {
      return this.getLunchWaiveInfoMessage(true)
    },

    secondLunchWaiveInfo() {
      return this.getLunchWaiveInfoMessage(false)
    },

    signedDuration() {
      return this.calculateDuration('work')
    },

    lunchDuration() {
      return this.calculateDuration('lunch')
    },

    secondLunchDuration() {
      return this.calculateDuration('second_lunch')
    },

    workDuration() {
      return this.signedDuration - this.lunchDuration - this.secondLunchDuration
    },

    // validators

    workEndInitialTime() {
      const nonScheduleInitialTimeConstraint = moment(this.shiftForm.work_start).add(510, 'minutes')

      const value = this.shiftForm.work_end ?? nonScheduleInitialTimeConstraint

      return this.resetSeconds(value)
    },

    validateWorkStart() {
      if (!this.shiftForm.work_start) {
        return 'Select a shift start time.'
      }

      return null
    },

    validateWorkEnd() {
      if (!this.shiftForm.work_end) {
        return 'Select a shift end time.'
      }

      if (!this.shiftForm.work_start) return null

      if (this.workDuration > this.maxWorkDuration) {
        return `Shorten the shift to less than ${this.minutesToHoursAndMinutes(
          this.maxWorkDuration
        )} or add a meal break of at least ${this.minLunchDuration} min.`
      }

      if (moment(this.shiftForm.work_start).isSameOrAfter(moment(this.shiftForm.work_end))) {
        return 'Choose a shift end after the shift start.'
      } else {
        return null
      }
    },

    lunchStartMinTime() {
      const value = this.shiftForm.work_start ?? this.shiftForm.scheduled_start

      return this.resetSeconds(value.clone().add(60, 'minutes'))
    },

    lunchStartInitialTime() {
      const value = this.shiftForm.lunch_start ?? this.estimated_lunch_start ?? this.lunchStartMinTime

      return this.resetSeconds(value)
    },

    lunchEndMinTime() {
      const value = moment(this.shiftForm.lunch_start).add(this.minLunchDuration, 'minutes')

      return this.resetSeconds(value)
    },

    lunchEndInitialTime() {
      const value = this.shiftForm.lunch_end ?? this.estimated_lunch_end ?? this.lunchEndMinTime

      return this.resetSeconds(value)
    },

    isValidFirstLunchStart() {
      const isStartValid = this.validateFirstLunchStart === null
      const isEndValid = this.validateFirstLunchEnd === null
      const isValid = isStartValid && isEndValid

      if (!isValid) {
        return false
      }

      if (this.lunchDuration < this.minLunchDuration || this.lunchDuration < 0) {
        return false
      }

      return true
    },

    validateFirstLunchStart() {
      if (!this.shiftForm.work_start || !this.shiftForm.work_end) {
        return null
      }

      if (!this.shiftForm.lunch_start) {
        if (this.shiftForm.lunch_end) {
          return 'Select a first meal break start time.'
        }

        return null
      }

      if (
        moment(this.shiftForm.lunch_start).isSameOrBefore(this.shiftForm.work_start) ||
        moment(this.shiftForm.lunch_start).isSameOrAfter(this.shiftForm.work_end)
      ) {
        return 'Choose a first meal break start during the shift.'
      } else if (
        this.shiftForm.lunch_end &&
        moment(this.shiftForm.lunch_start).isSameOrAfter(this.shiftForm.lunch_end)
      ) {
        return 'Choose a first meal break start before its end.'
      } else {
        return null
      }
    },

    validateFirstLunchEnd() {
      if (!this.shiftForm.work_start || !this.shiftForm.work_end) {
        return null
      }

      if (!this.shiftForm.lunch_end) {
        if (this.shiftForm.lunch_start) {
          return 'Select a first meal break end time.'
        }

        return null
      }

      if (!this.shiftForm.lunch_start) {
        return null
      }

      if (
        moment(this.shiftForm.lunch_end).isBefore(this.shiftForm.work_start) ||
        moment(this.shiftForm.lunch_end).isAfter(this.shiftForm.work_end)
      ) {
        return 'Choose a first meal break end during the shift.'
      } else if (this.shiftForm.lunch_end.isSameOrBefore(this.shiftForm.lunch_start)) {
        return 'Choose a first meal break end after its start.'
      } else if (this.lunchDuration < this.minLunchDuration) {
        return `First meal break must be at least ${this.minLunchDuration} min. Choose a later end time.`
      } else {
        return null
      }
    },

    isValidSecondLunchStart() {
      const isStartValid = this.validateSecondLunchStart === null
      const isEndValid = this.validateSecondLunchEnd === null
      const isValid = isStartValid && isEndValid

      if (!isValid) {
        return false
      }

      if (this.secondLunchDuration < this.minSecondLunchDuration || this.secondLunchDuration < 0) {
        return false
      }

      return true
    },

    secondLunchStartMinTime() {
      const value = this.shiftForm.lunch_end.clone().add(60, 'minutes')

      return this.resetSeconds(value)
    },

    secondLunchStartInitialTime() {
      const value =
        this.shiftForm.second_lunch_start ?? this.estimated_second_lunch_start ?? this.secondLunchStartMinTime

      return this.resetSeconds(value)
    },

    validateSecondLunchStart() {
      if (!this.shiftForm.work_start || !this.shiftForm.work_end) {
        return null
      }

      if (!this.shiftForm.second_lunch_start) {
        if (this.shiftForm.second_lunch_end) {
          return 'Select a second meal break start time.'
        }

        return null
      }

      const secondLunchRangeStart = this.shiftForm.lunch_end ?? this.shiftForm.work_start

      if (
        moment(this.shiftForm.second_lunch_start).isSameOrBefore(secondLunchRangeStart) ||
        moment(this.shiftForm.second_lunch_start).isSameOrAfter(this.shiftForm.work_end)
      ) {
        return this.shiftForm.lunch_end
          ? 'Choose a second meal break start after the first meal break and during the shift.'
          : 'Choose a second meal break start after the shift starts and during the shift.'
      } else if (
        this.shiftForm.second_lunch_end &&
        moment(this.shiftForm.second_lunch_start).isSameOrAfter(this.shiftForm.second_lunch_end)
      ) {
        return 'Choose a second meal break start before its end.'
      } else {
        return null
      }
    },

    secondLunchEndMinTime() {
      const value = moment(this.shiftForm.second_lunch_start).add(this.minSecondLunchDuration, 'minutes')

      return this.resetSeconds(value)
    },

    secondLunchEndInitialTime() {
      const value = this.shiftForm.second_lunch_end ?? this.estimated_second_lunch_end ?? this.secondLunchEndMinTime

      return this.resetSeconds(value)
    },

    validateSecondLunchEnd() {
      if (!this.shiftForm.work_start || !this.shiftForm.work_end) {
        return null
      }

      if (!this.shiftForm.second_lunch_end) {
        if (this.shiftForm.second_lunch_start) {
          return 'Select a second meal break end time.'
        }

        return null
      }

      if (!this.shiftForm.second_lunch_start) {
        return null
      }

      const secondLunchRangeStart = this.shiftForm.lunch_end ?? this.shiftForm.work_start

      if (
        moment(this.shiftForm.second_lunch_end).isSameOrBefore(secondLunchRangeStart) ||
        moment(this.shiftForm.second_lunch_end).isSameOrAfter(this.shiftForm.work_end)
      ) {
        return this.shiftForm.lunch_end
          ? 'Choose a second meal break end after the first meal break and during the shift.'
          : 'Choose a second meal break end after the shift starts and during the shift.'
      } else if (this.shiftForm.second_lunch_end.isSameOrBefore(this.shiftForm.second_lunch_start)) {
        return 'Choose a second meal break end after its start.'
      } else if (this.secondLunchDuration < this.minSecondLunchDuration) {
        return `Second meal break must be at least ${this.minSecondLunchDuration} min. Choose a later end time.`
      } else {
        return null
      }
    }
  },

  methods: {
    ...shiftActions(['getEstimatedHours']),

    refreshComputedDuration(value) {
      return value
    },

    calculateDuration(prefix) {
      const start_date = this.shiftForm[`${prefix}_start`]
      const end_date = this.shiftForm[`${prefix}_end`]

      if (!start_date || !end_date) {
        if (prefix === 'work') {
          this.shiftForm.duration = 0
        } else {
          this.shiftForm[`${prefix}_duration`] = 0
        }
        return 0
      }

      const start = moment(start_date).clone().startOf('minute')
      const end = moment(end_date).clone().startOf('minute')

      if (prefix === 'work') {
        this.shiftForm.duration = end.diff(start, 'minutes')
      } else {
        this.shiftForm[`${prefix}_duration`] = end.diff(start, 'minutes')
      }

      return end.diff(start, 'minutes')
    },

    calculateDisabledDateTimeRange(start, end, globalStart, globalEnd, pickerType) {
      const hourRange = this.range(0, 24)
      const minuteRange = this.range(0, 60)

      const adjustedStart = start ? moment.max(start, globalStart) : globalStart.clone()
      const adjustedEnd = end ? moment.min(end, globalEnd) : globalEnd.clone()

      let disabledHours = []
      let disabledMinutes = selectedHour => []

      if (pickerType === 'start') {
        disabledHours = hourRange.filter(hour => {
          if (adjustedStart.isSame(globalStart, 'day')) {
            return hour < globalStart.hours()
          }
          if (adjustedStart.isSame(globalEnd, 'day')) {
            return hour > globalEnd.hours()
          }
          return false
        })

        disabledMinutes = selectedHour => {
          if (adjustedStart.isSame(globalStart, 'day') && selectedHour === globalStart.hours()) {
            return minuteRange.filter(m => m < globalStart.minutes())
          }
          if (adjustedStart.isSame(globalEnd, 'day') && selectedHour === globalEnd.hours()) {
            return minuteRange.filter(m => m > globalEnd.minutes())
          }
          return []
        }
      }

      if (pickerType === 'end') {
        disabledHours = hourRange.filter(hour => {
          if (adjustedEnd.isSame(globalStart, 'day')) {
            return hour < globalStart.hours()
          }
          if (adjustedEnd.isSame(globalEnd, 'day')) {
            return hour > globalEnd.hours()
          }
          return false
        })

        disabledMinutes = selectedHour => {
          if (adjustedEnd.isSame(globalStart, 'day') && selectedHour === globalStart.hours()) {
            return minuteRange.filter(m => m < globalStart.minutes())
          }
          if (adjustedEnd.isSame(globalEnd, 'day') && selectedHour === globalEnd.hours()) {
            return minuteRange.filter(m => m > globalEnd.minutes())
          }
          return []
        }
      }

      return {
        disabledHours: () => disabledHours,
        disabledMinutes
      }
    },

    getDisabledTime(type, period) {
      const startKey = `${type}_start`
      const endKey = `${type}_end`

      return this.calculateDisabledDateTimeRange(
        this.shiftForm[startKey],
        this.shiftForm[endKey],
        this.shiftForm.work_start,
        this.shiftForm.work_end,
        period
      )
    },

    disabledLunchStartTime() {
      return this.getDisabledTime('lunch', 'start')
    },

    disabledLunchEndTime() {
      return this.getDisabledTime('lunch', 'end')
    },

    disabledSecondLunchStartTime() {
      return this.getDisabledTime('second_lunch', 'start')
    },

    disabledSecondLunchEndTime() {
      return this.getDisabledTime('second_lunch', 'end')
    },

    resetSeconds(date) {
      if (date) {
        return date.clone().seconds(0).milliseconds(0)
      }
      return date
    },

    range(start, end) {
      return start < end ? Array.from({ length: end - start }, (_, i) => i + start) : []
    },

    // disabled fields logic

    disabledSignedStartDate(date, condition) {
      if (!this.shift || !condition) return false

      const { work_start, work_end, scheduled_start, scheduled_end } = this.shift

      const useScheduledDates = !work_start && !work_end
      const start = useScheduledDates ? scheduled_start : work_start
      const end = useScheduledDates ? scheduled_end : work_end

      if (!start && !end) {
        return false
      }

      const mDate = moment(date).startOf('day')

      if (start && !end) {
        return !mDate.isSame(moment(start).startOf('day'), 'day')
      }

      if (start && end) {
        return mDate.isBefore(moment(start), 'day') || mDate.isAfter(moment(end), 'day')
      }

      if (!start && end) {
        return !mDate.isSame(moment(end).startOf('day'), 'day')
      }

      return true
    },

    disabledLunchStartDate(date) {
      if (!this.shiftForm.work_start || !this.shiftForm.work_end) {
        return true
      }

      return (
        this.shiftForm.work_start.isAfter(moment(date), 'day') || this.shiftForm.work_end.isBefore(moment(date), 'day')
      )
    },

    disabledLunchEndDate(date) {
      if (!this.shiftForm.work_start || !this.shiftForm.work_end || !this.shiftForm.lunch_start) {
        return true
      }

      return (
        this.shiftForm.work_end.isBefore(moment(date), 'day') || this.shiftForm.lunch_start.isAfter(moment(date), 'day')
      )
    },

    disabledSecondLunchStartDate(date) {
      if (!this.shiftForm.work_start || !this.shiftForm.work_end) {
        return true
      }

      return (
        this.shiftForm.work_start.isAfter(moment(date), 'day') || this.shiftForm.work_end.isBefore(moment(date), 'day')
      )
    },

    disabledSecondLunchEndDate(date) {
      if (!this.shiftForm.work_start || !this.shiftForm.work_end || !this.shiftForm.second_lunch_start) {
        return true
      }

      return (
        this.shiftForm.work_start.isAfter(moment(date), 'day') || this.shiftForm.work_end.isBefore(moment(date), 'day')
      )
    },

    getLunchWaiveInfoMessage(isFirstLunch) {
      const messages = []

      if (!this.isLunchWaiver) {
        messages.push('This associate does not have a meal break waiver.')

        return messages
      }

      if (isFirstLunch && !this.isLunchWaiveAvailable) {
        messages.push('An associate can waive the 1st meal break period if work time is 6 hours or less.')
      }

      if (!isFirstLunch && !this.isSecondLunchWaiveAvailable) {
        messages.push(
          'An associate can waive the 2nd meal break period if they work more than 10 but less than 12 hours, and only if they have already taken the 1st meal break.'
        )
      }

      if (!isFirstLunch) {
        messages.push('If the work hours exceed 12 hours, the 2nd meal break period cannot be waived.')
      }

      return messages
    },

    formatTimeFields(payload) {
      const formattedPayload = { ...payload }

      const timeFields = [
        'work_start',
        'work_end',
        'lunch_start',
        'lunch_end',
        'second_lunch_start',
        'second_lunch_end',
        'actual_lunch_end',
        'actual_lunch_start',
        'actual_second_lunch_end',
        'actual_second_lunch_start',
        'actual_work_end',
        'actual_work_start'
      ]

      timeFields.forEach(field => {
        if (formattedPayload[field]) {
          formattedPayload[field] = formattedPayload[field]
            ? moment.utc(formattedPayload[field]).tz(this.timeZone)
            : undefined
        }
      })

      return formattedPayload
    },

    async fillHours() {
      try {
        const {
          id: shift_id,
          work_start: start,
          work_end: end,
          lunch_start: lunch1_start,
          lunch_end: lunch1_end,
          second_lunch_start: lunch2_start,
          second_lunch_end: lunch2_end
        } = this.shiftForm

        const { duration, pay_hours, work_hours } = await this.getEstimatedHours({
          shift_id,
          start,
          end,
          lunch1_start,
          lunch1_end,
          lunch2_start,
          lunch2_end,
          calculation_type: 'provided'
        })

        const baseHours = {
          ...this.shiftForm.hours,
          duration,
          pay_hours,
          work_hours
        }

        if (this.shiftForm.set_scheduled_as_signed) {
          const scheduledHours = {
            scheduled_work_hours: work_hours,
            scheduled_pay_hours: pay_hours,
            scheduled_duration: duration
          }

          this.shiftForm.hours = {
            ...baseHours,
            ...scheduledHours
          }
        } else {
          this.shiftForm.hours = baseHours
        }
      } catch (error) {}
    }
  }
}
